<template>
	<Popover v-model:open="open">
		<PopoverAnchor as-child>
			<div class="w-full" @pointerdown="focusInputOnPointerDown">
				<Input
					:id="inputId"
					ref="search-input"
					v-model="search"
					:placeholder="placeholder"
					:class="[
						'h-8 rounded-[var(--border-radius)] border-[var(--border)] bg-background px-2 py-1 text-sm shadow-none selection:bg-[Highlight] selection:text-[HighlightText] focus-visible:border-[var(--border)] focus-visible:ring-0',
						inputClass,
					]"
					autocomplete="off"
					autocorrect="off"
					autocapitalize="off"
					spellcheck="false"
					@focus="open = true"
					@input="open = true"
					@keydown.down.prevent="highlightNextItem"
					@keydown.up.prevent="highlightPreviousItem"
					@keydown.enter="enterDown"
				/>
			</div>
		</PopoverAnchor>
		<PopoverContent
			align="start"
			:class="[
				'w-[var(--radix-popover-trigger-width)] overflow-hidden p-0',
				popoverClass,
			]"
			@open-auto-focus.prevent
			@interact-outside.prevent="interactOutside"
			@escape-key-down.prevent="closeWithoutCommit"
		>
			<div
				v-if="filteredOptions.length === 0"
				class="px-3 py-3 text-center text-sm"
			>
				{{ emptyStateLabel }}
			</div>
			<div v-else class="max-h-96 overflow-y-auto py-1">
				<button
					v-for="option in filteredOptions"
					:key="option.value"
					type="button"
					class="relative mx-1 flex w-[calc(100%-0.5rem)] cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm outline-hidden select-none"
					:class="{
						'bg-accent text-accent-foreground': isHighlightedOption(option),
					}"
					@pointermove="highlightOption(option)"
					@mousedown.prevent
					@click="selectOption(option.value)"
				>
					<div class="min-w-0 grow">
						<span class="block truncate">{{ option.label }}</span>
						<span
							v-if="option.meta"
							class="block truncate text-xs text-muted-foreground"
						>
							{{ option.meta }}
						</span>
					</div>
					<Check
						v-if="isSelectedOption(option)"
						class="ml-auto size-4 shrink-0"
					/>
				</button>
			</div>
		</PopoverContent>
	</Popover>
</template>

<script setup lang="ts">
import { useVModel } from "@vueuse/core";
import { Check } from "lucide-vue-next";
import type { FocusOutsideEvent, PointerDownOutsideEvent } from "reka-ui";
import type { HTMLAttributes } from "vue";
import { computed, nextTick, ref, useTemplateRef, watch } from "vue";
import { Input } from "@/components/ui/input";
import {
	Popover,
	PopoverAnchor,
	PopoverContent,
} from "@/components/ui/popover";

export interface AppPopoverSelectOption {
	value: string;
	label: string;
	meta?: string;
}

const props = withDefaults(
	defineProps<{
		modelValue?: string;
		searchValue?: string;
		options: AppPopoverSelectOption[];
		placeholder?: string;
		emptyStateLabel?: string;
		ignoreFilter?: boolean;
		autoOpen?: boolean;
		inputId?: string;
		inputClass?: HTMLAttributes["class"];
		popoverClass?: HTMLAttributes["class"];
	}>(),
	{
		placeholder: "Selecteer een optie",
		emptyStateLabel: "Geen opties gevonden",
		ignoreFilter: false,
		autoOpen: false,
		inputId: "popover-select-input",
	},
);

const emit = defineEmits<{
	(e: "update:modelValue", value: string | undefined): void;
	(e: "update:searchValue", value: string): void;
	(e: "select", value: string): void;
	(e: "close"): void;
}>();

const open = ref(props.autoOpen);
const searchInput = useTemplateRef("search-input");
const highlightedValue = ref<string>();
const search = useVModel(props, "searchValue", emit, {
	passive: true,
	defaultValue: "",
});

const selectedOption = computed(() =>
	props.options.find((option) => option.value === props.modelValue),
);
const filteredOptions = computed(() => {
	const normalizedSearch = (search.value ?? "").trim().toLowerCase();
	if (props.ignoreFilter || normalizedSearch === "") {
		return props.options;
	}

	return props.options.filter((option) => {
		const haystack = `${option.label} ${option.meta ?? ""}`.toLowerCase();
		return haystack.includes(normalizedSearch);
	});
});

watch(
	() => props.modelValue,
	() => {
		syncSearchWithSelectedOption();
	},
	{ immediate: true },
);

watch(filteredOptions, (nextOptions) => {
	syncHighlightedOption(nextOptions);
});

watch(
	open,
	(isOpen) => {
		if (!isOpen) {
			return;
		}

		void focusInput("tick");
		if (highlightedValue.value === undefined) {
			highlightedValue.value = filteredOptions.value[0]?.value;
		}
	},
	{ immediate: true },
);

function enterDown(e: KeyboardEvent) {
	e.preventDefault();
	if (highlightedValue.value) {
		selectOption(highlightedValue.value);
	}
}

function focusInputOnPointerDown() {
	void focusInput("frame");
}

function closeWithoutCommit() {
	syncSearchWithSelectedOption();
	open.value = false;
	emit("close");
}

function syncSearchWithSelectedOption() {
	if (selectedOption.value) {
		search.value = selectedOption.value.label;
		return;
	}

	if (props.modelValue === undefined) {
		search.value = "";
	}
}

function interactOutside(e: PointerDownOutsideEvent | FocusOutsideEvent) {
	if (isInputTarget(e.target)) {
		return;
	}

	closeWithoutCommit();
}

async function focusInput(when: "tick" | "frame") {
	if (when === "tick") {
		await nextTick();
	} else {
		await new Promise<void>((resolve) => {
			requestAnimationFrame(() => resolve());
		});
	}

	getInputElement()?.focus();
}

function isInputTarget(target: EventTarget | null) {
	return (
		target instanceof Node && (getInputElement()?.contains(target) ?? false)
	);
}

function getInputElement() {
	if (searchInput.value instanceof HTMLInputElement) {
		return searchInput.value;
	}

	return searchInput.value?.$el instanceof HTMLInputElement
		? searchInput.value.$el
		: undefined;
}

function selectOption(value: string) {
	const option = props.options.find((item) => item.value === value);
	highlightedValue.value = value;
	search.value = option?.label ?? "";
	emit("update:modelValue", value);
	emit("select", value);
	open.value = false;
	emit("close");
}

function syncHighlightedOption(options: AppPopoverSelectOption[]) {
	highlightedValue.value =
		options.find((option) => option.value === highlightedValue.value)?.value ??
		options[0]?.value;
}

function highlightOption(option: AppPopoverSelectOption) {
	highlightedValue.value = option.value;
}

function isHighlightedOption(option: AppPopoverSelectOption) {
	return option.value === highlightedValue.value;
}

function isSelectedOption(option: AppPopoverSelectOption) {
	return option.value === props.modelValue;
}

function moveHighlightedOption(offset: number) {
	if (filteredOptions.value.length === 0) {
		highlightedValue.value = undefined;
		return;
	}

	const currentIndex = filteredOptions.value.findIndex(isHighlightedOption);
	const nextIndex =
		currentIndex === -1
			? offset > 0
				? 0
				: filteredOptions.value.length - 1
			: Math.max(
					0,
					Math.min(filteredOptions.value.length - 1, currentIndex + offset),
				);

	highlightedValue.value = filteredOptions.value[nextIndex]?.value;
}

function highlightNextItem() {
	open.value = true;
	moveHighlightedOption(1);
	void focusInput("frame");
}

function highlightPreviousItem() {
	open.value = true;
	moveHighlightedOption(-1);
	void focusInput("frame");
}
</script>
